/**
 * A piece of UI state the app remembers until the end of the day.
 *
 * Filters and periods are working context, not preferences. Coming back an
 * hour later and finding the Business tab still showing «Команда» for last
 * quarter is helpful; opening it tomorrow morning and finding the same thing
 * is a puzzle — you have to notice the stale control before you can trust the
 * number next to it.
 *
 * So the memory is scoped to the calendar day rather than a rolling window: a
 * new day always starts on the defaults, and that is a rule you can hold in
 * your head. The day is the browser's own local date, which is Kyiv, the same
 * clock the rest of the app quotes.
 *
 * Use it for anything with a visible control that changes what the numbers
 * mean. Do not use it for text the user typed — a search box that refills
 * itself looks like a bug.
 */

import { useCallback, useEffect, useRef, useState } from "react";

interface Stored<T> {
  v: T;
  /** Local calendar day this was written on, ISO. */
  d: string;
}

function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function read<T>(key: string): T | undefined {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw) as Stored<T>;
    if (!parsed || typeof parsed !== "object" || parsed.d !== today()) return undefined;
    return parsed.v;
  } catch {
    /* corrupt or unavailable storage — fall through to the default */
    return undefined;
  }
}

export function useRemembered<T>(key: string, initial: T | (() => T)): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    const stored = read<T>(key);
    if (stored !== undefined) return stored;
    return typeof initial === "function" ? (initial as () => T)() : initial;
  });

  // Yesterday's entry is dead weight once it has been ignored; clearing it on
  // mount keeps localStorage from collecting one row per filter per lifetime.
  const cleaned = useRef(false);
  useEffect(() => {
    if (cleaned.current) return;
    cleaned.current = true;
    if (read<T>(key) === undefined) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        /* nothing to do and nothing worth reporting */
      }
    }
  }, [key]);

  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify({ v: next, d: today() } satisfies Stored<T>));
      } catch {
        /* private mode / quota — state still works for this session */
      }
    },
    [key],
  );

  return [value, set];
}
